import clsx from 'clsx';
import { Button } from 'common/Components/Button';
import Captcha from 'common/Components/Captcha';
import Input from 'common/Components/form/Input';
import { PasswordInput } from 'common/Components/form/paswordInput';
import { useRouter } from 'common/hooks/useRouter';
import { useRouterExternal } from 'common/hooks/useRouterExternal';
import { PersonIcon } from 'common/Icons';
import {
	handleProductType,
	setCookies,
	stringToBase64,
	toEnglishNumber,
} from 'common/method/helper';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { isShowCaptchaInLoginAction } from 'redux/common/autorizationSlice';
import { RootState } from 'redux/store';
import { useAuthorization, useGetCaptch } from 'services/login';

// query param the backend uses to report a failed SSO login on the way back
const SSO_ERROR_PARAM = 'message';

type formDate = {
	username: string;
	password: string;
	captchaValue: string;
};

const FormLogin = () => {
	//*global
	const [searchParams, setSearchParams] = useSearchParams();
	const { navigate } = useRouter();
	const {
		autorizationSlice: { isShowCaptchaInLogin, showLoginWithBrokersSSO }
	} = useSelector((state: RootState) => state);

	const dispatch = useDispatch();

	const { t } = useTranslation();

	const pt = handleProductType(searchParams);

	const { data: captcha, refetch: refetchCaptcha } = useGetCaptch();
	const { pushExternal } = useRouterExternal();

	const handleRefetchCaptch = (): void => {
		refetchCaptcha();
		resetField('captchaValue');
	};

	//*form
	const {
		register,
		handleSubmit,
		formState: { errors },
		resetField,
		setValue,
		watch,
		getValues,
	} = useForm<formDate>({
		defaultValues: {
			username: '',
			password: '',
			captchaValue: '',
		},
		mode: 'onChange',
	});
	const handletwoStepLogin = (
		token: string,
		retryToken: string,
		starredMessage: string,
		expireDate: string
	) => {
		navigate.push('/login/twoFactor', {
			state: { retryToken, starredMessage, expireDate, temporaryToken: token },
		});
	};

	const doneLogin = ({
		token,
		callBackUrl,
	}: Pick<responseAuthorizationType, 'token' | 'callBackUrl'>) => {
		setCookies(token);
		pushExternal(callBackUrl, token);
	};

	const onSubmit: SubmitHandler<formDate> = (data) => {
		if (isShowCaptchaInLogin && !captcha?.key) {
			handleRefetchCaptch();
			alert('hasnt key captcha');
			return;
		}

		const reqData: reqAuthorizationType = {
			password: stringToBase64(data.password),
			captchaValue: toEnglishNumber(data.captchaValue),
			captchaKey: captcha?.key,
			term: data.username,
			callBackUrl: '',
			productType: pt,
			requestUri: '',
			token: '',
		};
		mutateLogin(reqData);
	};

	//*Authorization
	const { mutate: mutateLogin } = useAuthorization({
		onSuccess: (data) => {
			const {
				callBackUrl,
				token,
				loginResultType,
				hasFinger,
				showCaptcha,
				showLoginWithBrokersSSO,
				twoStep: { retryToken, status, starredMessage, expireDate },
			} = data.result;


			dispatch(isShowCaptchaInLoginAction({ showCaptcha, hasFinger, showLoginWithBrokersSSO }));
			if (data.succeeded) {
				if (!status) {
					doneLogin({ callBackUrl, token });
				} else {
					retryToken &&
						starredMessage &&
						expireDate &&
						handletwoStepLogin(token, retryToken, starredMessage, expireDate);
				}
			} else {
				handleRefetchCaptch();

				if (loginResultType) {
					toast.error(t('Errors.' + loginResultType));
				}
				if (loginResultType === 'ForceChangePassword') {
					navigate.push(`/force-change-password`, {
						state: { temperoryToken: token },
					});
				}
			}
		},
		onError: (err) => {
			handleRefetchCaptch();
			console.log('err', err);
		},
	});

	//*login with brokers SSO
	// the backend redirects the user back to the login page with `SSO_ERROR_PARAM=<code>`
	// in the query params whenever the SSO operation fails
	const ssoErrorCode = searchParams.get(SSO_ERROR_PARAM);

	useEffect(() => {
		if (!ssoErrorCode) return;

		toast.error(
			t('Errors.' + ssoErrorCode, { defaultValue: t('Errors.UnknownError') })
		);

		const restParams = new URLSearchParams(searchParams);
		restParams.delete(SSO_ERROR_PARAM);
		setSearchParams(restParams, { replace: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ssoErrorCode]);


	const handleLoginWithFarabi = () => {
		const OauthUrl = window?.api?.API_BASE_ROUTE;
		window.location.href = OauthUrl + '/ExternalSSO/v1/Login?productType=Trader';
	}


	return (
		<>
			<h1 className="text-5xl font-bold text-center text-L-primary-50 mt-6 pb-6">
				{t(`BrokerCode_${window.api?.BROKER_CODE}.FormSide.LogInText`)}
			</h1>
			<form onSubmit={handleSubmit(onSubmit)}>
				<Input
					{...register('username', {
						required: {
							value: true,
							message: t('Errors.Input.Required', {
								n: t('FormSide.Input.Username.Label'),
								interpolation: { escapeValue: false },
							}),
						},
					})}
					placeholder={t(`FormSide.Input.Username.Placeholder`)}
					classparent="mt-3"
					classInput="ltrInsert h-[48px]"
					type={'text'}
					label={t(`settingPageItem.userName_label`)}
					data-cy="username"
					// iconend={
					//   <KeyboardIcon className="text-L-gray-150 dark:text-D-gray-150" />
					// }
					textError={errors.username?.message}
					iconstart={<PersonIcon />}
				/>

				<div className="mt-3">
					<PasswordInput
						register={register}
						error={errors.password}
						setValues={setValue}
						name="password"
						labelInput={t(`FormSide.Input.Password.Label`)}
						placeHolder={t(`FormSide.Input.Password.Placeholder`)}
						watch={watch}
					/>
				</div>

				<div
					onClick={() => navigate.push('/forget-password/request')}
					className={clsx(
						'text-L-gray-200 text-base float-left cursor-pointer relative top-[-20px]',
					)}
				>
					{t(`FormSide.ForgetPasswordLabel`)}
				</div>

				{isShowCaptchaInLogin && (
					<div className="mt-5">
						<Captcha
							register={register}
							errors={errors}
							captcha={captcha}
							onRefresh={handleRefetchCaptch}
							setValue={setValue}
							getValues={getValues}
						/>
					</div>
				)}

				<div
					className={clsx({
						'mt-36': !isShowCaptchaInLogin,
						'mt-8': isShowCaptchaInLogin,
					})}
				>
					<Button
						data-cy="submit"
						className="w-full bg-L-secondary-50 rounded-2xl text-L-basic dark:text-D-basic p-3 text-3xl mb-4"
					>
						{t(`FormSide.Confirm.Login`)}
					</Button>

					{(showLoginWithBrokersSSO && [189, 1].includes(window.api?.BROKER_CODE)) && !(pt?.toLowerCase().includes('kahkeshan')) && (
						<Button
							type='button'
							data-cy="button"
							className="w-full bg-[#187C48] rounded-2xl text-white dark:text-D-basic p-3 text-3xl mb-6"
							onClick={handleLoginWithFarabi}
						>
							ورود با حساب فارابی
						</Button>
					)}
					{(Boolean(window.api.ACCOUNT_ACTIVATION) && window.api?.BROKER_CODE === 189) && (
						<div className='flex items-center justify-center'>

							<button
								onClick={() =>
									// navigate.push('/account-activation')
									window.location.href = 'https://help.irfarabi.com/rivas'
								}
								type='button'
								className="block !font-medium text-center text-L-primary-50 text-xl md:text-b2xl lg:text-2xl mb-2 rounded-2xl"
							>
								{t(
									`BrokerCode_${window.api?.BROKER_CODE}.Activation.account_request`
								)}
							</button>
						</div>
					)}
				</div>
			</form>
		</>
	);
};

export default FormLogin;
